import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GridWrapper } from './styles';
import { CellClickedEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, ValueGetterParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { IIncidentReport } from '@nkr/models/index';
import CheckboxRenderer from '@nkr/components/checkbox-renderer';
import { IRowData } from './models';
import { Glyphicon } from 'react-bootstrap';
import { Utility } from '@nkr/services/utility';
import * as moment from 'moment';


interface IProps {
    data: IRowData[],
    onGridReady: (e: GridReadyEvent) => void,
    onEdit: (id: number) => void,
    onSelectAllRows: () => void,
    onRowSelected: (id: number, isSelected: boolean) => void
}

const IncidentListGrid = (props: IProps) => {
    const columns: ColDef[] = [
        {
            field: 'isSelected',
            headerName: '',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><Glyphicon glyph='check' title='Check/Uncheck All Rows' onClick={props.onSelectAllRows} /></SortableHeaderComponent>,
            width: 55,
            cellClass: 'donor-connect-checkbox',
            sortable: false,
            suppressSizeToFit: true,
            cellRendererParams: { onValueChange: props.onRowSelected },
            cellRenderer: 'checkboxRenderer'
        },

        {
            headerName: '',
            cellClass: 'action-menu',
            headerClass: 'donor-connect-list-header',
            field: 'id',
            hide: true,
            width: 50
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Incident #</SortableHeaderComponent>,
            headerName: 'Incident #',
            field: 'id',
            width: 85
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            headerName: 'Center',
            field: 'centerName',
            width: 150
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Candidate First Name</SortableHeaderComponent>,
            headerName: 'Candidate First Name',
            field: 'candidateFirstName',
            comparator: Utility.nameComparator,
            width: 200
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Candidate Last Name</SortableHeaderComponent>,
            headerName: 'Candidate Last Name',
            field: 'candidateLastName',
            comparator: Utility.nameComparator,
            width: 200
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Mentor</SortableHeaderComponent>,
            headerName: 'Mentor',
            field: 'assignedMentorName',
            width: 200
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Category</SortableHeaderComponent>,
            headerName: 'Category',
            field: 'incidentCategoryType',
            width: 200
        },

        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Origin</SortableHeaderComponent>,
            headerName: 'Origin',
            field: 'incidentOriginType',
            width: 200
        },
      
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Incident Date</SortableHeaderComponent>,
            headerName: 'Incident Date',
            field: 'incidentDate',
            width: 150,
            sortable: true,
            comparator: (date1: string, date2: string) => {
                // Parse the dates using moment or any other date parsing library
                const parsedDate1 = moment(date1, 'MM/DD/YYYY');
                const parsedDate2 = moment(date2, 'MM/DD/YYYY');

                if (parsedDate1.isValid() && parsedDate2.isValid()) {
                    return parsedDate1.isSameOrBefore(parsedDate2) ? -1 : 1;
                } else {
                    return 0;
                }
            }
        },
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
            headerName: 'Incident Status',
            field: 'incidentStatus',
            width: 200
        },
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                columnDefs={columnDefs}
                defaultColDef={{
                    cellClass: 'donor-connect-list-cell',
                    headerClass: 'donor-connect-list-header',
                    sortable: true,
                }}
                rowData={rowData}
                rowHeight={30}
                suppressRowClickSelection={true}
                suppressCellSelection={true}
                suppressDragLeaveHidesColumns={true}
                immutableData={true}
                onGridReady={props.onGridReady}
                onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                getRowNodeId={(data) => data.id.toString()}
                frameworkComponents={{
                    checkboxRenderer: CheckboxRenderer
                }}
                onCellClicked={(params: CellClickedEvent) => {
                    if (params.colDef.field != 'isSelected') {
                        let data = params.data as IIncidentReport;
                        props.onEdit(data.id);
                    }
                }}
            />
        </GridWrapper>
    );
}

export default IncidentListGrid;